import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIsDesktop } from '../../hooks/useIsDeskTop';
import {
  Image,
  Keyboard,
  ScrollView,
  Text,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import { MAX_WIDTH } from '../../constants/platform';
import MyAccountMenu from '../../components/MyAccountMenu/MyAccountMenu';
import PageTitle from '../../components/PageTitle';
import { COLORS, FONTS } from '../../constants';
import { TextInput } from '../../components/TextInput/TextInput';
import { Button } from '../../components/Button';
import { Footer } from '../../components/Footer';
import { NavigationService } from '../../services/navigation';
import ChatRoomApi from '../../services/api/chatroom.api';
import { Alert } from '../../services/alert';
import { IChatRoom, IMessage } from '../../types/chat.types';
import { MessagesProps } from './types';
import { Loading } from '../../components';
import { showLastSendTime } from '../../services/utils';
import PusherSingleton from '../../services/pusher/PusherSingleton';
import { debounce } from 'lodash';
import { IUser } from '../../types/auth.types';
import UsersApi from '../../services/api/user.api';
import { KeyboardAvoidingView } from 'react-native';
import createPusherClient from '../../services/utils/pusher';
import { useSelector } from 'react-redux';
import { RootState } from '../../services/redux';
import { useFocusEffect } from '@react-navigation/native';
import StaffPopup from './components/StaffPopup';
import { htmlDecode } from '../../services/utils/htmlDecode';

const UserItem: FC<{ user: IUser; handleCreateChat: (id: number) => void }> = ({
  user,
  handleCreateChat,
}) => {
  return (
    <TouchableOpacity
      onPress={() => {
        handleCreateChat(user.id);
      }}
      style={{
        paddingHorizontal: 15,
        paddingVertical: 10,
        borderBottomWidth: 1,
        borderBottomColor: COLORS.LGREY,
        flexDirection: 'row',
      }}
    >
      <Image
        source={{ uri: user.avatar_url }}
        style={{
          width: 45,
          height: 45,
          borderRadius: 45,
          marginRight: 10,
        }}
      />
      <Text
        style={{
          color: COLORS.BLACK,
          fontSize: 14,
          fontFamily: FONTS.AvenirRegular,
        }}
        numberOfLines={1}
      >
        {`${user.first_name} ${user.last_name}`}
      </Text>
    </TouchableOpacity>
  );
};

const Messages: FC<MessagesProps> = ({ navigation }) => {
  const isDesktop = useIsDesktop();

  const { access_token, user } = useSelector(
    (redux: RootState) => redux.authReducer
  );

  const userDropdownRef = useRef<View>(null);
  const [openDropdown, setOpenDropdown] = useState<boolean>(false);
  const [rooms, setRooms] = useState<IChatRoom[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fullScreenLoading, setFullScreenLoading] = useState<boolean>(false);
  const [searchUser, setSearchUser] = useState<string>('');
  const [users, setUsers] = useState<IUser[]>([]);
  const [isLoadingUser, setIsLoadingUser] = useState<boolean>(false);
  const [pusherRooms, setPusherRooms] = useState<IChatRoom[]>([]);
  const [staffPopup, setStaffPopup] = useState<boolean>(false);

  const handleOpenOnlineStaff = () => {
    setStaffPopup(true);
  };

  const handleCreateSupportStaffChat = useCallback(async () => {
    setFullScreenLoading(true);
    ChatRoomApi.postCreateChatRoom()
      .then((res) => {
        NavigationService.push('MessagesUser', {
          id: res.data.data.id,
        });
      })
      .catch((error) => {
        Alert.alert('Error', error.meta.message);
      })
      .finally(() => {
        setFullScreenLoading(false);
      });
  }, []);

  const handleCreateChatByUser = useCallback(async (id: number) => {
    setFullScreenLoading(true);
    ChatRoomApi.postCreateChatRoom({ staff_id: id })
      .then((res) => {
        NavigationService.push('MessagesUser', {
          id: res.data.data.id,
        });
      })
      .catch((error) => {
        Alert.alert('Error', error.meta.message);
      })
      .finally(() => {
        setFullScreenLoading(false);
      });
  }, []);

  const getChatRooms = useCallback(async () => {
    setIsLoading(true);
    ChatRoomApi.getChatRoom()
      .then((res) => {
        setRooms(res.data.data.data);
        setPusherRooms(res.data.data.data);
      })
      .catch((error) => {
        console.log(error);
        // Alert.alert('Error', error.meta.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handleSearch = useCallback(
    async (text: string) => {
      setIsLoadingUser(true);
      UsersApi.getUsers({ search: text, staff: 1 })
        .then((res) => {
          setUsers(res.data.data.data);
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoadingUser(false);
        });
    },
    [setIsLoadingUser]
  );

  const renderMessagesList = useMemo(() => {
    return (
      <View
        style={{
          flexDirection: 'column',
        }}
      >
        {rooms.map((item, index) => {
          return (
            <TouchableOpacity
              style={{
                flexDirection: 'row',
                paddingLeft: 20,
                paddingVertical: 20,
                borderBottomWidth: 1,
                borderBottomColor: '#BCBCBC2E',
                justifyContent: 'space-between',
                paddingRight: 20,
                alignItems: 'center',
              }}
              onPress={() => {
                NavigationService.push('MessagesUser', {
                  id: item.id,
                });
              }}
            >
              <View
                style={{
                  width: isDesktop() ? '50%' : '70%',
                  flexDirection: 'row',
                  gap: 15,
                  alignItems: 'center',
                }}
              >
                <Image
                  source={{ uri: item.avatar }}
                  style={{
                    width: 55,
                    height: 55,
                    borderRadius: 55,
                  }}
                />
                <View
                  style={{
                    flexDirection: 'column',
                  }}
                >
                  <Text
                    style={{
                      fontFamily: FONTS.AvenirMedium,
                      fontWeight: '700',
                      fontSize: 19,
                      color: COLORS.BLACK,
                      letterSpacing: 1,
                    }}
                  >
                    {item.name_html}
                  </Text>
                  <View style={{ width: isDesktop() ? 350 : 200 }}>
                    <Text
                      style={{
                        fontFamily: FONTS.AvenirMedium,
                        fontWeight: '500',
                        fontSize: 16,
                        color: '#A5A5A5',
                      }}
                      numberOfLines={1}
                    >
                      {item.last_message
                        ? `${
                            item.last_message.user_id === user?.id
                              ? 'You: '
                              : ''
                          }${
                            item.last_message.type === 'image'
                              ? 'Send a photo'
                              : htmlDecode(item.last_message.content)
                          }`
                        : ''}
                    </Text>
                  </View>
                </View>
              </View>
              <View
                style={{
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                }}
              >
                <Text
                  style={{
                    fontFamily: FONTS.AvenirMedium,
                    fontWeight: '500',
                    fontSize: 16,
                    color: '#A5A5A5',
                    marginBottom: 5,
                  }}
                >
                  {item?.last_message
                    ? showLastSendTime(item.last_message.created_at)
                    : ''}
                </Text>
                {item.unread_messages > 0 && (
                  <View
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 20,
                      backgroundColor: COLORS.RED_PRIMARY,
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: 10,
                    }}
                  >
                    <Text
                      style={{
                        fontFamily: FONTS.AvenirMedium,
                        fontWeight: '600',
                        fontSize: 12,
                        color: COLORS.WHITE,
                      }}
                    >
                      {`${item.unread_messages}`}
                    </Text>
                  </View>
                )}
              </View>
            </TouchableOpacity>
          );
        })}
      </View>
    );
  }, [isDesktop, rooms, user?.id]);

  const debouncedSearch = debounce(handleSearch, 500);

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      getChatRooms();
    });

    return unsubscribe;
  }, [getChatRooms, navigation]);

  useFocusEffect(
    useCallback(() => {
      // Screen is focused (user is on the screen)
      const pusher = PusherSingleton.getInstance(
        access_token ? access_token : ''
      );
      const channels = pusherRooms.map((room) => {
        const channel = pusher.pusher.subscribe(`private-App.Rooms.${room.id}`);

        channel.bind('pusher:subscription_succeeded', () => {
          console.log(
            `Successfully subscribed to channel: ${`private-App.Rooms.${room.id}`}`
          );
        });

        channel.bind('pusher:subscription_error', (status: any) => {
          console.error(
            `Subscription error on ${`private-App.Rooms.${room.id}`}:`,
            status
          );
        });
        channel.bind(
          'room-message-sent',
          (data: { message: IMessage; room: IChatRoom }) => {
            setRooms((prevRooms) => {
              const updatedRooms = prevRooms.map((item) =>
                item.id === data.room.id
                  ? {
                      ...item,
                      unread_messages: item.unread_messages + 1,
                      last_message: data.message,
                    }
                  : item
              );

              const roomWithNewMessage = updatedRooms.find(
                (room) => room.id === data.room.id
              );
              return roomWithNewMessage
                ? [
                    roomWithNewMessage,
                    ...updatedRooms.filter((room) => room.id !== data.room.id),
                  ]
                : updatedRooms;
            });
          }
        );
        return channel;
      });

      return () => {
        channels.forEach((channel) => {
          channel.unbind_all();
          pusher.pusher.unsubscribe(channel.name);
          console.log(`unsubcribe ${channel.name}`);
        });
        console.log('Cleanup finished');
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [access_token, pusherRooms])
  );

  // useEffect(() => {
  //   const pusher = PusherSingleton.getInstance(
  //     access_token ? access_token : ''
  //   );
  //   const channels = rooms.map((room) => {
  //     const channel = pusher.pusher.subscribe(`private-App.Rooms.${room.id}`);

  //     channel.bind('pusher:subscription_succeeded', () => {
  //       console.log(
  //         `Successfully subscribed to channel: ${`private-App.Rooms.${room.id}`}`
  //       );
  //     });

  //     channel.bind('pusher:subscription_error', (status: any) => {
  //       console.error(
  //         `Subscription error on ${`private-App.Rooms.${room.id}`}:`,
  //         status
  //       );
  //     });
  //     channel.bind(
  //       'room-message-sent',
  //       (data: { message: IMessage; room: IChatRoom }) => {
  //         setRooms((prevRooms) => {
  //           const updatedRooms = prevRooms.map((item) =>
  //             item.id === data.room.id
  //               ? {
  //                   ...item,
  //                   unread_messages: item.unread_messages + 1,
  //                   last_message: data.message,
  //                 }
  //               : item
  //           );

  //           const roomWithNewMessage = updatedRooms.find(
  //             (room) => room.id === data.room.id
  //           );
  //           return roomWithNewMessage
  //             ? [
  //                 roomWithNewMessage,
  //                 ...updatedRooms.filter((room) => room.id !== data.room.id),
  //               ]
  //             : updatedRooms;
  //         });
  //       }
  //     );
  //     return channel;
  //   });

  //   return () => {
  //     channels.forEach((channel) => {
  //       channel.unbind_all();
  //       pusher.pusher.unsubscribe(channel.name);
  //       console.log(`unsubcribe ${channel.name}`);
  //     });
  //     console.log('Cleanup finished');
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [access_token]);

  useEffect(() => {
    handleSearch('');
  }, [handleSearch]);

  if (isDesktop()) {
    return (
      <ScrollView showsVerticalScrollIndicator={false}>
        <TouchableWithoutFeedback
          style={{
            position: 'relative',
          }}
          onPress={() => {
            setOpenDropdown(false);
          }}
        >
          <View
            style={{
              maxWidth: MAX_WIDTH,
              width: '90%',
              marginHorizontal: 'auto',
              paddingTop: 40,
              marginBottom: 200,
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
              }}
            >
              <View style={{ width: '22%', minWidth: 230, marginRight: 20 }}>
                <MyAccountMenu />
              </View>
              <View style={{ width: '70%' }}>
                <PageTitle
                  title="YOUR MESSAGES"
                  haveGoBackButton={true}
                  handleGoBack={() => {
                    NavigationService.push('Root', {
                      screen: 'MenuTab',
                      params: {
                        screen: 'Menu',
                      },
                    });
                  }}
                />
                <View
                  style={{
                    flexDirection: 'column',
                    backgroundColor: COLORS.WHITE,
                    shadowColor: '#F0F0F0',
                    shadowOffset: { width: 0, height: 12 },
                    shadowOpacity: 1,
                    shadowRadius: 32,
                    elevation: 20,
                    borderRadius: 18,
                    padding: 20,
                  }}
                >
                  <Text
                    style={{
                      fontFamily: FONTS.Arial,
                      fontWeight: '700',
                      fontSize: 20,
                      letterSpacing: 2,
                      textTransform: 'uppercase',
                      marginBottom: 15,
                    }}
                  >
                    Chats
                  </Text>
                  <View
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'center',
                      marginBottom: 15,
                    }}
                  >
                    <Button
                      arrowRight={false}
                      text="MESSAGE HELPING HAND SUPPORT STAFF"
                      style={{
                        paddingVertical: 13,
                        paddingHorizontal: 120,
                        backgroundColor: COLORS.LIGHT_RED,
                        borderRadius: 16,
                      }}
                      className=""
                      textStyle={{
                        fontFamily: FONTS.AvenirMedium,
                        fontWeight: '500',
                        fontSize: 12,
                        color: COLORS.RED_PRIMARY,
                        letterSpacing: 1.8,
                      }}
                      onPress={handleOpenOnlineStaff}
                    />
                  </View>
                  <View
                    style={{
                      position: 'relative',
                      marginBottom: 20,
                    }}
                  >
                    <TextInput
                      placeholder="Search"
                      placeholderTextColor={'#888888'}
                      style={{
                        borderRadius: 5,
                        backgroundColor: '#F0F0F0',
                        paddingLeft: 45,
                        paddingVertical: 10,
                        fontFamily: FONTS.AvenirMedium,
                        fontWeight: '500',
                        fontSize: 16,
                      }}
                      onFocus={() => {
                        setOpenDropdown(true);
                      }}
                      onChangeText={(text) => {
                        setOpenDropdown(true);
                        debouncedSearch(text);
                      }}
                    />
                    <Image
                      source={require('../../assets/search_white.png')}
                      style={{
                        width: 18,
                        height: 18,
                        position: 'absolute',
                        tintColor: '#888888',
                        top: 11,
                        left: 20,
                      }}
                    />
                  </View>
                  {openDropdown && (
                    <ScrollView
                      nestedScrollEnabled={true}
                      style={{
                        zIndex: 2,
                        position: 'absolute',
                        top: 160,
                        backgroundColor: 'white',
                        borderColor: COLORS.LGREY,
                        borderWidth: 1,
                        borderRadius: 5,
                        maxHeight: 400,
                        left: 20,
                        right: 20,
                      }}
                    >
                      {isLoadingUser && (
                        <View style={{ padding: 10 }}>
                          <Loading />
                        </View>
                      )}
                      {!isLoadingUser &&
                        users.length > 0 &&
                        users.map((user) => (
                          <UserItem
                            user={user}
                            handleCreateChat={handleCreateChatByUser}
                          />
                        ))}
                    </ScrollView>
                  )}
                  <View
                    style={{
                      marginBottom: 40,
                    }}
                  >
                    <View
                      style={{
                        marginBottom: 20,
                      }}
                    >
                      {isLoading && (
                        <View style={{ marginTop: 20 }}>
                          <Loading />
                        </View>
                      )}
                      {isLoading === false &&
                        rooms.length !== 0 &&
                        renderMessagesList}
                    </View>
                  </View>
                </View>
              </View>
            </View>
          </View>
        </TouchableWithoutFeedback>
        <Footer />
        <StaffPopup
          isModalVisible={staffPopup}
          setIsModalVisible={setStaffPopup}
          handleCreateChat={handleCreateChatByUser}
        />
        {fullScreenLoading && <Loading isFullScreen />}
      </ScrollView>
    );
  }

  return (
    <ScrollView showsVerticalScrollIndicator={false} nestedScrollEnabled={true}>
      <TouchableWithoutFeedback
        style={{
          position: 'relative',
        }}
        onPress={() => {
          setOpenDropdown(false);
        }}
      >
        <KeyboardAvoidingView>
          <PageTitle
            title={'YOUR MESSAGES'}
            haveGoBackButton={true}
            handleGoBack={() => {
              NavigationService.push('Root', {
                screen: 'MenuTab',
                params: {
                  screen: 'Menu',
                },
              });
            }}
          />
          <View style={{ paddingTop: 20, paddingHorizontal: 20, zIndex: 2 }}>
            <Text
              style={{
                fontFamily: FONTS.Arial,
                fontWeight: '700',
                fontSize: 20,
                letterSpacing: 2,
                textTransform: 'uppercase',
                marginBottom: 15,
              }}
            >
              Chats
            </Text>
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'center',
                marginBottom: 15,
              }}
            >
              <Button
                arrowRight={false}
                text="MESSAGE HELPING HAND SUPPORT STAFF"
                style={{
                  paddingVertical: 13,
                  paddingHorizontal: 30,
                  backgroundColor: COLORS.LIGHT_RED,
                  borderRadius: 16,
                }}
                className=""
                textStyle={{
                  fontFamily: FONTS.AvenirMedium,
                  fontWeight: '500',
                  fontSize: 11,
                  color: COLORS.RED_PRIMARY,
                  letterSpacing: 1.8,
                }}
                onPress={handleOpenOnlineStaff}
              />
            </View>
            <View>
              <TextInput
                placeholder="Search"
                placeholderTextColor={'#888888'}
                style={{
                  borderRadius: 5,
                  backgroundColor: '#F0F0F0',
                  paddingLeft: 45,
                  height: 46,
                  fontFamily: FONTS.AvenirMedium,
                  fontWeight: '500',
                  fontSize: 16,
                }}
                onFocus={() => {
                  setOpenDropdown(true);
                }}
                onChangeText={(text) => {
                  setOpenDropdown(true);
                  debouncedSearch(text);
                }}
              />
              <Image
                source={require('../../assets/search_white.png')}
                style={{
                  width: 18,
                  height: 18,
                  position: 'absolute',
                  tintColor: '#888888',
                  top: 13,
                  left: 20,
                }}
              />
            </View>
          </View>
          {openDropdown && (
            <ScrollView
              nestedScrollEnabled={true}
              style={{
                zIndex: 2,
                position: 'absolute',
                top: 230,
                backgroundColor: 'white',
                borderColor: COLORS.LGREY,
                borderWidth: 1,
                borderRadius: 5,
                maxHeight: 400,
                left: 20,
                right: 20,
              }}
            >
              {isLoadingUser && (
                <View style={{ padding: 10 }}>
                  <Loading />
                </View>
              )}
              {!isLoadingUser &&
                users.length > 0 &&
                users.map((user) => (
                  <UserItem
                    user={user}
                    handleCreateChat={handleCreateChatByUser}
                  />
                ))}
            </ScrollView>
          )}
          <View
            style={{
              marginBottom: 20,
            }}
          >
            {isLoading && (
              <View style={{ marginTop: 20 }}>
                <Loading />
              </View>
            )}
            {isLoading === false && rooms.length !== 0 && renderMessagesList}
          </View>
          <Footer />
          <StaffPopup
            isModalVisible={staffPopup}
            setIsModalVisible={setStaffPopup}
            handleCreateChat={handleCreateChatByUser}
          />
          {fullScreenLoading && <Loading isFullScreen />}
        </KeyboardAvoidingView>
      </TouchableWithoutFeedback>
    </ScrollView>
  );
};

export default Messages;
